import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getBuiltinPulseMapBySlug } from '@shared/builtinPulseMaps';
import { getBuiltinCirclesMapBySlug } from '@shared/circlesBuiltinMaps';
import { buildEngineConfig, step, type EngineInput } from '@shared/engine/pulse/core';
import { createInitialState } from '@shared/engine/pulse/state';
import {
  buildCirclesEngineConfig,
  step as circlesStep,
  type CirclesEngineInput,
} from '@shared/engine/circles/core';
import { createCirclesInitialState } from '@shared/engine/circles/state';
import { type CirclesChart } from '@shared/circlesChart';
import { GameRenderer } from '../game/rendering/renderer';
import { CirclesRenderer } from '../game/circles/circlesRenderer';
import type {
  CirclesFlashSpec,
  CirclesFloatingText,
  CirclesParticleEmission,
  CirclesScorePop,
  CirclesShockwave,
} from '../game/circles/render/circlesRenderTypes';
import { buildPerfectReplayInputs } from '../game/session';

interface SyntheticJuice {
  particleEmissions: CirclesParticleEmission[];
  flashes: CirclesFlashSpec[];
  floatingTexts: CirclesFloatingText[];
  shockwaves: CirclesShockwave[];
  scorePops: CirclesScorePop[];
  /**
   * Amount of HP to subtract from the engine state before drawing,
   * used to fake a damage event in the harness so the renderer's
   * frame-to-frame HP comparison fires the bleed segment, bar shake,
   * and numeric pulse animations. `null` for non-damage juice.
   */
  hpDrop: number | null;
}

/**
 * Build synthetic juice payloads for the harness. Returns empty arrays
 * for `juice === 'none'`; otherwise stages a representative payload of
 * each FX type (floating text, shockwaves, particles, flashes) that the
 * renderer would emit naturally on a hit/miss/damage event during real
 * play. Output is then passed straight into `renderer.draw` so the
 * captured PNG shows what the FX look like layered over a real chart
 * snapshot at the requested beat.
 *
 * Anchors all "near the action" juice to the first upcoming hit object
 * (i.e. the most likely target of an imaginary input around the
 * requested beat). HP-loss juice anchors just below the top-centre HP
 * bar (via `renderer.getHudHpDamageAnchor()`) so the synthetic damage
 * composition matches what players see in-game: bleed-segment,
 * bar-shake, and the "-N HP" floater all read as one coherent event.
 */
function buildSyntheticJuice(
  juice: string,
  chart: CirclesChart,
  beat: number,
  renderer: CirclesRenderer,
): SyntheticJuice {
  const empty: SyntheticJuice = {
    particleEmissions: [],
    flashes: [],
    floatingTexts: [],
    shockwaves: [],
    scorePops: [],
    hpDrop: null,
  };
  if (juice === 'none') {
    return empty;
  }
  // Prefer the first upcoming hit object so juice anchors near where
  // the player's eyes are; for late-chart beats (e.g. `07-finale`,
  // captured at totalBeats - 1) fall back to the *last* hit object so
  // the juice still appears in the playfield region that just held the
  // chart's final note, not all the way back at the chart's spatial
  // origin (which is what `hitObjects[0]` would give).
  const target =
    chart.hitObjects.find((o) => o.beat >= beat) ??
    chart.hitObjects[chart.hitObjects.length - 1];
  if (!target) {
    return empty;
  }
  const anchor = renderer.playfieldToCss(target.x, target.y);
  const baseR = renderer.osuLengthToCss(chart.circleRadius);
  const out: SyntheticJuice = {
    particleEmissions: [],
    flashes: [],
    floatingTexts: [],
    shockwaves: [],
    scorePops: [],
    hpDrop: null,
  };
  if (juice === 'miss') {
    out.particleEmissions.push({ type: 'hit_miss', x: anchor.x, y: anchor.y });
    out.flashes.push({ type: 'miss', t: 0.22, intensity: 1 });
    out.floatingTexts.push({
      text: 'Miss',
      x: anchor.x,
      y: anchor.y - (baseR + 14),
      colour: '#ff3b5c',
      vy: -50,
      scale: 1,
      life: 0.65,
      maxLife: 0.65,
      big: false,
    });
    out.shockwaves.push({
      x: anchor.x,
      y: anchor.y,
      startR: baseR * 1.2,
      endR: baseR * 3.8,
      life: 0.32,
      maxLife: 0.36,
      thickness: Math.max(2, baseR * 0.14),
      colour: '#ff3b5c',
      alpha: 0.7,
    });
    return out;
  }
  if (juice === 'perfect') {
    out.particleEmissions.push({
      type: 'hit_perfect',
      x: anchor.x,
      y: anchor.y,
    });
    out.flashes.push({ type: 'perfect', t: 0.28, intensity: 1.1 });
    out.floatingTexts.push({
      text: 'PERFECT!',
      x: anchor.x,
      y: anchor.y - (baseR + 14),
      colour: '#3bff9d',
      vy: -50,
      scale: 1,
      life: 0.8,
      maxLife: 0.8,
      big: false,
    });
    out.shockwaves.push({
      x: anchor.x,
      y: anchor.y,
      startR: baseR * 1.0,
      endR: baseR * 4.5,
      life: 0.3,
      maxLife: 0.32,
      thickness: Math.max(2, baseR * 0.18),
      colour: '#3bff9d',
      alpha: 0.85,
    });
    const scoreAnchor = renderer.getHudScoreAnchor();
    out.scorePops.push({
      text: '+300',
      startX: anchor.x,
      startY: anchor.y,
      targetX: scoreAnchor.x,
      targetY: scoreAnchor.y,
      colour: '#3bff9d',
      big: false,
      life: 0.55,
      maxLife: 0.7,
    });
    return out;
  }
  if (juice === 'damage') {
    out.flashes.push({ type: 'hp_loss', t: 0.36, intensity: 1.0 });
    // Mirror the live session's HP-loss floater anchor (just below
    // the top-centre HP bar) so the harness shows the same damage
    // composition the player sees in-game.
    const damageAnchor = renderer.getHudHpDamageAnchor();
    out.floatingTexts.push({
      text: '-5 HP',
      x: damageAnchor.x,
      y: damageAnchor.y,
      colour: '#ff3b5c',
      vy: -55,
      scale: 0.95,
      life: 0.95,
      maxLife: 1.0,
      big: true,
    });
    // Trigger the HP bar's bleed-segment / shake / numeric-pulse by
    // dropping engine HP. The bleed reads off `lastShownHp` vs the
    // current state, and the harness wires both via the renderer's
    // `testOnlyPrimeHpForHarness` hook below.
    out.hpDrop = 5;
    return out;
  }
  return empty;
}

/**
 * Frame-deterministic render harness.
 *
 * Renders a single frame of any builtin chart (Pulse or Circles) at any
 * beat with a chosen input strategy ("none" -> player has done nothing
 * yet; "perfect" -> player has played the perfect replay up to that
 * beat). Used by the renderFrames CLI / Playwright tests so AI agents
 * can inspect canonical moments without playing the game in real time.
 *
 * Query params:
 *   mode   - "pulse" | "circles" (default "pulse")
 *   slug   - builtin chart slug (e.g. "on-and-on"). NB: Pulse and
 *            Circles builtin maps share the same baseSlug — `mode`
 *            disambiguates.
 *   beat   - target beat (can be negative to inspect the lead-in camera)
 *   inputs - "none" | "perfect" (default "none"). "perfect" is only
 *            supported for Pulse today; Circles always renders with no
 *            inputs replayed.
 *   width  - canvas CSS width in px (default 1920)
 *   height - canvas CSS height in px (default 1080)
 *   juice  - "none" | "miss" | "perfect" | "damage" (circles only,
 *            default "none"). Injects synthetic FX (floating text,
 *            shockwaves, flashes, particles) so the harness can
 *            capture how juice reads on a static frame — without
 *            this every screenshot of circles mode looks identical
 *            to a paused playthrough with no recent input. Skipped
 *            on `mode=pulse` (Circles doesn't have the same FX vocabulary).
 *
 * Signals:
 *   window.__renderDone = true  once the frame has been drawn.
 *   window.__renderError = string  if anything went wrong.
 *
 * The page intentionally bypasses the AuthProvider / TopNav so background
 * fetches don't pollute the canvas or delay the render-done signal.
 */
export function RenderDebugPage() {
  const [params] = useSearchParams();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string>('');

  const mode = params.get('mode') ?? 'pulse';
  const slug = params.get('slug') ?? 'on-and-on';
  const targetBeat = Number(params.get('beat') ?? '0');
  const inputs = params.get('inputs') ?? 'none';
  const width = Number(params.get('width') ?? '1920');
  const height = Number(params.get('height') ?? '1080');
  const juice = params.get('juice') ?? 'none';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      if (mode !== 'pulse' && mode !== 'circles') {
        throw new Error(`bad mode: ${mode} (expected "pulse" or "circles")`);
      }
      if (!Number.isFinite(targetBeat)) throw new Error(`bad beat: ${targetBeat}`);
      if (!Number.isFinite(width) || width <= 0) throw new Error(`bad width: ${width}`);
      if (!Number.isFinite(height) || height <= 0) throw new Error(`bad height: ${height}`);

      if (mode === 'circles') {
        const circlesBuiltin = getBuiltinCirclesMapBySlug(slug);
        if (!circlesBuiltin) throw new Error(`unknown circles slug: ${slug}`);
        if (inputs !== 'none') {
          throw new Error(`circles mode only supports inputs=none today (got ${inputs})`);
        }
        if (!['none', 'miss', 'perfect', 'damage'].includes(juice)) {
          throw new Error(`bad juice: ${juice} (expected none|miss|perfect|damage)`);
        }
        const chart = circlesBuiltin.chart;
        const config = buildCirclesEngineConfig(chart);
        const state = createCirclesInitialState(chart);
        const stepTo = Math.max(0, targetBeat);
        const noInputs: CirclesEngineInput[] = [];
        if (stepTo > 0) {
          circlesStep({ config, state, fromBeat: 0, toBeat: stepTo, inputs: noInputs });
        }
        const renderer = new CirclesRenderer(canvas);
        // Synthetic FX injection — gives the harness a way to capture
        // how juice reads against the current renderer state without
        // requiring the engine to actually have produced these events
        // from real input. Anchored to the first visible upcoming
        // hit-object for spatially-anchored juice (Miss, perfect
        // burst, particles); HP-loss juice anchors below the top HP
        // bar so the floater rises into the same area that holds the
        // bleed-segment, bar shake, and numeric pulse.
        const fx = buildSyntheticJuice(juice, chart, targetBeat, renderer);
        if (fx.hpDrop !== null) {
          // Prime the renderer's "previous HP" to the pre-damage
          // value, then drop the engine state. The very next draw
          // sees `state.hp < lastShownHp` and triggers the bleed
          // segment + bar shake + numeric pulse exactly as a real
          // damage event would.
          const priorHp = state.hp;
          state.hp = Math.max(0, state.hp - fx.hpDrop);
          renderer.testOnlyPrimeHpForHarness(priorHp);
        }
        renderer.draw({
          chart,
          state,
          beat: targetBeat,
          width,
          height,
          cursor: null,
          cursorPressed: false,
          particleEmissions: fx.particleEmissions,
          flashes: fx.flashes,
          floatingTexts: fx.floatingTexts,
          shockwaves: fx.shockwaves,
          scorePops: fx.scorePops,
          shake: { amount: 0, decay: 0 },
          beatPulse: 0,
          countdown: targetBeat < 0 ? Math.max(0, -targetBeat) : null,
          paused: false,
        });
        setInfo(
          `mode=circles slug=${slug} beat=${targetBeat} inputs=${inputs} juice=${juice} ${width}x${height} ` +
            `hitObjects=${chart.hitObjects.length} state.alive=${state.alive}`,
        );
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            (window as unknown as { __renderDone?: boolean }).__renderDone = true;
          });
        });
        return;
      }

      const builtin = getBuiltinPulseMapBySlug(slug);
      if (!builtin) throw new Error(`unknown slug: ${slug}`);
      const chart = builtin.chart;

      // Build engine inputs to advance state to target beat.
      let engineInputs: EngineInput[] = [];
      if (inputs === 'perfect') {
        engineInputs = buildPerfectReplayInputs(chart);
      } else if (inputs !== 'none') {
        throw new Error(`bad inputs: ${inputs} (expected "none" or "perfect")`);
      }

      const config = buildEngineConfig(chart);
      const state = createInitialState(chart);
      // Step from 0 to targetBeat (or just leave state at 0 for negative).
      // Negative beats are valid - they represent the lead-in camera position
      // *before* the song has started, so we just don't run the engine.
      const stepTo = Math.max(0, targetBeat);
      if (stepTo > 0) {
        step({
          config,
          state,
          fromBeat: 0,
          toBeat: stepTo,
          inputs: engineInputs.filter((i) => i.beat <= stepTo),
        });
      }

      const renderer = new GameRenderer(canvas);
      renderer.draw({
        chart,
        state,
        beat: targetBeat,
        width,
        height,
        particleEmissions: [],
        flashes: [],
        floatingTexts: [],
        shake: { amount: 0, decay: 0 },
        beatPulse: 0,
        flipAnim: 0,
        countdown: targetBeat < 0 ? Math.max(0, -targetBeat) : null,
        paused: false,
        showTapHint: false,
      });

      setInfo(
        `mode=pulse slug=${slug} beat=${targetBeat} inputs=${inputs} ${width}x${height} ` +
          `state.alive=${state.alive} state.surface=${state.currentSurface} ` +
          `pads=${chart.pads.length}`,
      );
      // Signal Playwright that the frame is ready. Two browser frames are
      // enough for the canvas to commit + GPU to flush in headless Chromium.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          (window as unknown as { __renderDone?: boolean }).__renderDone = true;
        });
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      (window as unknown as { __renderError?: string }).__renderError = msg;
      (window as unknown as { __renderDone?: boolean }).__renderDone = true;
    }
  }, [mode, slug, targetBeat, inputs, width, height]);

  return (
    <div
      style={{
        margin: 0,
        padding: 0,
        background: '#000',
        // Use 100% (= html clientWidth) rather than 100vw so we respect the
        // scrollbar gutter reserved on <html> in base.css. 100vw would push
        // a scrollbar-width sliver past the html content box and produce a
        // spurious horizontal scrollbar that shows up in screenshot frames.
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <canvas
        ref={canvasRef}
        data-testid="render-debug-canvas"
        width={width}
        height={height}
        style={{
          width: `${width}px`,
          height: `${height}px`,
          display: 'block',
        }}
      />
      <div
        data-testid="render-debug-info"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          padding: '4px 8px',
          background: 'rgba(0,0,0,0.5)',
          color: error ? '#ff4d6d' : '#0f0',
          font: '11px monospace',
          pointerEvents: 'none',
        }}
      >
        {error ?? info}
      </div>
    </div>
  );
}
